import { motion } from 'framer-motion'
import { useAgents } from '../hooks/useAgents'
import { AGENT_CONFIG } from '../types'
import type { Agent } from '../types'

// Calculate minutes since last heartbeat
function getMinutesSince(lastSeen: string | null): number {
  if (!lastSeen) return Infinity
  const last = new Date(lastSeen)
  const now = new Date()
  return (now.getTime() - last.getTime()) / (1000 * 60)
}

// Get pulse intensity based on heartbeat recency
function getPulseIntensity(minutes: number): number {
  if (minutes <= 30) return 1.0      // Bright - active
  if (minutes <= 60) return 0.6      // Dim - warning
  return 0.3                          // Fading - stale
}

// Get ring thickness based on observation velocity
function getRingThickness(obsCount: number): number {
  const base = 3
  const scale = Math.min(obsCount / 100, 4)
  return base + scale
}

// Get glow intensity multiplier
function getGlowIntensity(minutes: number): number {
  if (minutes <= 30) return 1.2      // Strong glow
  if (minutes <= 60) return 0.7      // Medium glow
  return 0.3                          // Weak glow
}

// Individual Orbital Ring Component
function OrbitalRing({ 
  agent, 
  radius, 
  delay = 0 
}: { 
  agent: Agent
  radius: number
  delay?: number
}) {
  const config = AGENT_CONFIG[agent.agent_id] || {
    name: agent.agent_id,
    color: '#888888',
    ringClass: 'default-ring'
  }

  const minutesSince = getMinutesSince(agent.last_seen)
  const intensity = getPulseIntensity(minutesSince)
  const glowIntensity = getGlowIntensity(minutesSince)
  const thickness = getRingThickness(agent.obs_count)
  const isStale = minutesSince > 60

  const size = radius * 2
  const circumference = 2 * Math.PI * radius
  const dashArray = `${circumference * 0.25} ${circumference * 0.75}`

  return (
    <motion.div 
      className="group cursor-pointer"
      style={{
        position: 'absolute',
        width: size,
        height: size,
        marginLeft: -radius,
        marginTop: -radius,
        left: '50%',
        top: '50%',
      }}
      initial={{ opacity: 0, scale: 0.8 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ 
        duration: 0.5, 
        delay: delay,
        ease: [0.25, 0.46, 0.45, 0.94]
      }}
    >
      {/* Outer glow effect - enhanced with multiple layers */}
      <motion.div
        style={{
          position: 'absolute',
          width: size,
          height: size,
          borderRadius: '50%',
          background: `radial-gradient(circle, ${config.color}${Math.round(intensity * 40).toString(16).padStart(2, '0')} 0%, transparent 60%)`,
        }}
        animate={{
          opacity: [intensity * 0.4, intensity * 0.8, intensity * 0.4],
          scale: [1, 1.08, 1],
        }}
        transition={{
          duration: 3,
          repeat: Infinity,
          ease: "easeInOut",
          delay: delay,
        }}
      />

      {/* Secondary glow ring */}
      <motion.div
        style={{
          position: 'absolute',
          width: size * 0.9,
          height: size * 0.9,
          left: '5%',
          top: '5%',
          borderRadius: '50%',
          border: `1px solid ${config.color}`,
          opacity: 0.1,
        }}
        animate={{
          opacity: [0.05 * glowIntensity, 0.15 * glowIntensity, 0.05 * glowIntensity],
          scale: [0.95, 1.02, 0.95],
        }}
        transition={{
          duration: 4,
          repeat: Infinity,
          ease: "easeInOut",
          delay: delay + 0.5,
        }}
      />

      {/* Rotating ring SVG */}
      <motion.svg
        style={{
          position: 'absolute',
          left: 0,
          top: 0,
          width: size,
          height: size,
        }}
        viewBox={`0 0 ${size} ${size}`}
        animate={{ rotate: 360 }}
        transition={{
          duration: 20 + delay * 5,
          repeat: Infinity,
          ease: "linear",
        }}
      >
        {/* Background ring (dim) */}
        <circle
          cx={radius}
          cy={radius}
          r={radius - thickness}
          fill="none"
          stroke={config.color}
          strokeWidth={thickness}
          strokeOpacity={0.08}
        />
        
        {/* Active pulsing arc - enhanced with glow */}
        <motion.circle
          cx={radius}
          cy={radius}
          r={radius - thickness}
          fill="none"
          stroke={config.color}
          strokeWidth={thickness}
          strokeLinecap="round"
          strokeDasharray={dashArray}
          style={{
            filter: `drop-shadow(0 0 ${6 * glowIntensity}px ${config.color})`,
          }}
          animate={{
            strokeOpacity: [intensity * 0.5, intensity * 1.2, intensity * 0.5],
            strokeWidth: [thickness, thickness + 2, thickness],
          }}
          transition={{
            duration: 2,
            repeat: Infinity,
            ease: "easeInOut",
            delay: delay,
          }}
        />
      </motion.svg>

      {/* Agent label - centered with enhanced styling */}
      <motion.div
        style={{
          position: 'absolute',
          left: '50%',
          top: '50%',
          transform: 'translate(-50%, -50%)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          pointerEvents: 'none',
        }}
        initial={{ opacity: 0, scale: 0.8, y: 10 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ 
          duration: 0.5, 
          delay: delay + 0.3,
          ease: [0.25, 0.46, 0.45, 0.94]
        }}
      >
        <motion.span 
          style={{ 
            color: config.color,
            fontSize: '1.125rem',
            fontWeight: 700,
            letterSpacing: '0.05em',
            textShadow: `0 0 ${10 * glowIntensity}px ${config.color}80, 0 0 20px rgba(0,0,0,0.8)`,
          }}
          animate={{
            textShadow: [
              `0 0 ${8 * glowIntensity}px ${config.color}60, 0 0 20px rgba(0,0,0,0.8)`,
              `0 0 ${16 * glowIntensity}px ${config.color}90, 0 0 20px rgba(0,0,0,0.8)`,
              `0 0 ${8 * glowIntensity}px ${config.color}60, 0 0 20px rgba(0,0,0,0.8)`,
            ]
          }}
          transition={{
            duration: 2,
            repeat: Infinity,
            ease: "easeInOut",
            delay: delay,
          }}
        >
          {config.name}
        </motion.span>
        <motion.span 
          style={{ 
            fontSize: '0.75rem', 
            color: '#6b7280', 
            marginTop: '4px',
          }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: delay + 0.5 }}
        >
          {agent.obs_count} obs
        </motion.span>
        {isStale && (
          <motion.span 
            style={{ 
              fontSize: '10px', 
              color: '#f87171', 
              marginTop: '2px',
              textShadow: '0 0 8px rgba(248, 113, 113, 0.5)',
            }} 
            animate={{ opacity: [0.5, 1, 0.5] }}
            transition={{ duration: 1.5, repeat: Infinity }}
          >
            stale
          </motion.span>
        )}
      </motion.div>

      {/* Enhanced glass tooltip on hover */}
      <motion.div 
        style={{
          position: 'absolute',
          left: '50%',
          top: '100%',
          transform: 'translateX(-50%)',
          marginTop: '16px',
          padding: '12px 16px',
          borderRadius: '12px',
          background: 'rgba(15, 23, 42, 0.85)',
          backdropFilter: 'blur(16px)',
          WebkitBackdropFilter: 'blur(16px)',
          border: `1px solid ${config.color}30`,
          whiteSpace: 'nowrap',
          opacity: 0,
          pointerEvents: 'none',
          zIndex: 50,
          boxShadow: `0 8px 32px rgba(0, 0, 0, 0.4), 0 0 20px ${config.color}20`,
        }}
        className="group-hover:opacity-100"
        initial={{ y: -10, opacity: 0 }}
        whileHover={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.2 }}
      >
        <div style={{ fontSize: '0.75rem', color: '#d1d5db' }}>
          <div style={{ color: config.color, fontWeight: 600, marginBottom: '4px' }}>
            {config.name}
          </div>
          <div>Last seen: {agent.last_seen ? new Date(agent.last_seen).toLocaleString() : 'Never'}</div>
          <div>Actions: {agent.action_count}</div>
          {agent.success_rate !== null && (
            <div>Success: {agent.success_rate}%</div>
          )}
          {agent.pending_actions > 0 && (
            <div style={{ color: '#facc15' }}>Pending: {agent.pending_actions}</div>
          )}
        </div>
      </motion.div>
    </motion.div>
  )
}

// Main Agent Pulse Component
export default function AgentPulse() {
  const { agents, loading, error } = useAgents()

  if (loading) {
    return (
      <motion.div 
        style={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center', 
          width: '24rem', 
          height: '24rem',
        }}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
      >
        <motion.div
          style={{
            width: '4rem',
            height: '4rem',
            border: '3px solid rgba(55, 65, 81, 0.3)',
            borderTopColor: '#00d4ff',
            borderRadius: '50%',
            boxShadow: '0 0 20px rgba(0, 212, 255, 0.3)',
          }}
          animate={{ rotate: 360 }}
          transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
        />
      </motion.div>
    )
  }

  if (error) {
    return (
      <motion.div 
        style={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          justifyContent: 'center', 
          width: '24rem', 
          height: '24rem', 
          color: '#f87171' 
        }}
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.3 }}
      >
        <div style={{ textAlign: 'center' }}>
          <motion.div 
            style={{ fontSize: '2.25rem', marginBottom: '1rem' }}
            animate={{ 
              textShadow: [
                '0 0 10px rgba(248, 113, 113, 0.5)',
                '0 0 20px rgba(248, 113, 113, 0.8)',
                '0 0 10px rgba(248, 113, 113, 0.5)',
              ]
            }}
            transition={{ duration: 2, repeat: Infinity }}
          >
            ⚠️
          </motion.div>
          <div style={{ fontSize: '0.875rem' }}>{error}</div>
          <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.5rem' }}>
            Is the API running on localhost:3001?
          </div>
        </div>
      </motion.div>
    )
  }

  if (agents.length === 0) {
    return (
      <motion.div 
        style={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center', 
          width: '24rem', 
          height: '24rem', 
          color: '#6b7280' 
        }}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
      >
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '2.25rem', marginBottom: '1rem' }}>📡</div>
          <div style={{ fontSize: '0.875rem' }}>No agents found</div>
        </div>
      </motion.div>
    )
  }

  // Sort agents in order: jc, hal, sterling
  const sortedAgents = [...agents].sort((a, b) => {
    const order = ['jc', 'hal', 'sterling']
    return order.indexOf(a.agent_id.toLowerCase()) - order.indexOf(b.agent_id.toLowerCase())
  })

  // Ring sizes (largest outer to smallest inner)
  const ringSizes = [140, 100, 60]

  return (
    <motion.div 
      style={{ position: 'relative', width: '320px', height: '320px' }}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      {/* Background glow - enhanced */}
      <motion.div 
        style={{
          position: 'absolute',
          inset: 0,
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(100,100,100,0.08) 0%, transparent 70%)',
        }}
        animate={{
          scale: [1, 1.05, 1],
          opacity: [0.5, 0.8, 0.5],
        }}
        transition={{
          duration: 4,
          repeat: Infinity,
          ease: "easeInOut",
        }}
      />

      {/* Center hub - enhanced with glow */}
      <motion.div
        style={{
          position: 'absolute',
          width: '2rem',
          height: '2rem',
          borderRadius: '50%',
          background: 'linear-gradient(135deg, #1f2937 0%, #374151 100%)',
          border: '1px solid #4b5563',
          left: '50%',
          top: '50%',
          marginLeft: '-1rem',
          marginTop: '-1rem',
          zIndex: 10,
        }}
        animate={{
          boxShadow: [
            '0 0 20px rgba(100,100,100,0.3)',
            '0 0 40px rgba(100,100,100,0.5)',
            '0 0 20px rgba(100,100,100,0.3)',
          ],
        }}
        transition={{
          duration: 4,
          repeat: Infinity,
          ease: "easeInOut",
        }}
      />

      {/* Orbital rings - all centered */}
      {sortedAgents.map((agent, index) => (
        <OrbitalRing
          key={agent.agent_id}
          agent={agent}
          radius={ringSizes[index] || 80}
          delay={index * 0.5}
        />
      ))}

      {/* Connection lines with enhanced glow */}
      <svg 
        style={{ 
          position: 'absolute', 
          inset: 0, 
          width: '100%', 
          height: '100%', 
          pointerEvents: 'none', 
          opacity: 0.3 
        }}
      >
        <defs>
          <filter id="line-glow" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="2" result="coloredBlur"/>
            <feMerge>
              <feMergeNode in="coloredBlur"/>
              <feMergeNode in="SourceGraphic"/>
            </feMerge>
          </filter>
        </defs>
        <line 
          x1="50%" y1="50%" x2="50%" y2="10%" 
          stroke="#444" strokeWidth="1" strokeDasharray="4 4"
          filter="url(#line-glow)"
        />
        <line 
          x1="50%" y1="50%" x2="85%" y2="70%" 
          stroke="#444" strokeWidth="1" strokeDasharray="4 4"
          filter="url(#line-glow)"
        />
        <line 
          x1="50%" y1="50%" x2="15%" y2="70%" 
          stroke="#444" strokeWidth="1" strokeDasharray="4 4"
          filter="url(#line-glow)"
        />
      </svg>
    </motion.div>
  )
}
